/**
 * useVehicleRecordRealtime - Tempo real da ficha do veículo (por registro)
 *
 * Assina apenas `customer_vehicles` filtrado por `id = <veículo aberto>`.
 * O servidor do Realtime aplica o filtro e a RLS (customer_vehicles_select por
 * tenant), então eventos de outros veículos/tenants não chegam ao navegador.
 *
 * A cada mudança do veículo (status, dados, foto, vínculo), invalida:
 *   - ['vehicle-details', tenantId, vehicleId]  -> cabeçalho, status e resumo
 *   - ['vehicle-timeline', tenantId, vehicleId] -> histórico/andamentos
 *   - ['tenant-vehicles'] e ['tenant-vehicles-kpis'] -> lista e números da listagem
 *
 * Também assina INSERT em `stock_movements` do tenant (RLS por membro): qualquer
 * instalação/retirada, inclusive feita em outro computador, atualiza a aba Equipamentos.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-11 v1.2.0 - stock_movements via useTenantStockRealtime (canal único); invalida aba O.S.
 *   RELEASE: 2026-10-09 v1.1.0 - Escuta stock_movements para atualizar Equipamentos sem F5.
 *   RELEASE: 2026-10-02 v1.0.0 - Criação inicial: status do veículo atualiza sem F5
 *            quando alterado pelo Workflow, por outra pessoa ou por outra aba.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTenantStockRealtime } from '@/hooks/realtime/useTenantStockRealtime';

const logger = createLogger('useVehicleRecordRealtime');

interface Params {
  /** Id do veículo aberto na ficha */
  vehicleId?: string | null;
  /** Habilita a assinatura (ficha aberta) */
  enabled?: boolean;
}

export function useVehicleRecordRealtime({ vehicleId, enabled = true }: Params) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  // v1.2.0: movimentações de estoque pelo canal único do tenant (sem canal duplicado)
  useTenantStockRealtime([
    ['vehicle-equipment', tenantId, vehicleId],
    ['vehicle-timeline', tenantId, vehicleId],
    ['vehicle-details', tenantId, vehicleId],
    ['service-orders'],
  ], enabled && !!vehicleId);

  useEffect(() => {
    if (!tenantId || !vehicleId || !enabled) return;

    // Canal por registro: somente eventos deste veículo chegam ao cliente
    const channel = supabase
      .channel(`vehicle-record-${vehicleId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customer_vehicles',
          filter: `id=eq.${vehicleId}`,
        },
        (payload) => {
          logger.debug('Mudança do veículo recebida', { evento: payload.eventType, vehicleId });
          queryClient.invalidateQueries({ queryKey: ['vehicle-details', tenantId, vehicleId] });
          queryClient.invalidateQueries({ queryKey: ['vehicle-timeline', tenantId, vehicleId] });
          queryClient.invalidateQueries({ queryKey: ['tenant-vehicles'] });
          queryClient.invalidateQueries({ queryKey: ['tenant-vehicles-kpis'] });
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          logger.warn('Falha na assinatura do veículo', { vehicleId });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [vehicleId, tenantId, enabled, queryClient]);
}
